/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import emptyStateStyles from './sh-empty-state.lit.scss.js';
/**
 * @prop {String} label - Defines the text shown below the icon. If left undefined, no text will be shown.
 * @prop {String} icon - Defines the icon shown above the text. If left undefined, no icon will be shown.
 * @prop {Boolean} dragAndDrop - Defines the icon shown above the text. If left undefined, no icon will be shown.
 * @prop {String} iconColor - Sets the color of the icon added through `icon` property.
 * @attr {String} icon-color - Sets the color of the icon added through `icon` property.
 * @attr {Boolean} drag-and-drop - Add border to drag and drop area.
 * @slot footer - Takes Buttons or Text Links as children.
 */

export class SHEmptyState extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      /** @ignore */
      dragAndDrop: { type: Boolean, reflect: true, attribute: 'drag-and-drop' },
      /** @ignore */
      _emptyFooter: { type: Boolean },
      iconColor: { type: String, reflect: true, attribute: 'icon-color' },
    };
  }

  static get styles() {
    return [sharedStyles, emptyStateStyles];
  }

  render() {
    return html`
      ${this.icon ? html`<sh-icon icon="${this.icon}" .color="${this.iconColor}"></sh-icon>` : ''}
      <sh-text size="body-1">${this.label}</sh-text>
      <div class="footer-wrapper" ?empty-footer="${this._emptyFooter}">
        <slot
          name="footer"
          @slotchange="${this._handleSlotNodes.bind(this)}"
          id="footerSlot"
        ></slot>
      </div>
    `;
  }

  constructor() {
    super();
    this._emptyFooter = true;
  }

  updated(changedProperties) {
    const listOfProperties = ['icon', 'label', 'dragAndDrop'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  _handleSlotNodes() {
    const footerNodesLength = this.shadowRoot.querySelector('#footerSlot').assignedNodes({
      flatten: true,
    }).length;
    this._emptyFooter = footerNodesLength === 0;
  }
}

customElements.define('sh-empty-state', SHEmptyState);
